import React, { useState, useRef, useEffect, useCallback } from "react";
import { Info } from "lucide-react";

interface TooltipProps {
  /** The content to show in the tooltip */
  content: string;
  /** The children element that triggers the tooltip (required if showInfoIcon is false) */
  children?: React.ReactNode;
  /** Position of the tooltip relative to the trigger */
  position?: "top" | "bottom" | "left" | "right";
  /** Whether to show an info icon instead of wrapping children */
  showInfoIcon?: boolean;
}

/**
 * Tooltip
 * A reusable tooltip component that displays information on click.
 * Optimized for mobile devices where hover is not available.
 * Can wrap any element or show an info icon.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "top",
  showInfoIcon = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position based on trigger element
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spacing = 8; // Space between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - spacing;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + spacing;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - spacing;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + spacing;
        break;
    }

    // Keep tooltip within viewport bounds
    if (left < 8) left = 8;
    if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    if (top < 8) top = 8;
    if (top + tooltipRect.height > viewportHeight - 8) {
      top = viewportHeight - tooltipRect.height - 8;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [isVisible, calculatePosition]);

  // Handle click outside to close tooltip
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(target)
      ) {
        setIsVisible(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsVisible(false);
      }
    };

    // Add listeners with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isVisible]);

  // Handle window resize
  useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isVisible, calculatePosition]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible((prev) => !prev);
  }, []);

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="fixed z-50 max-w-xs min-w-[120px] rounded-md bg-gray-900 px-3 py-2 text-sm text-white shadow-lg wrap-break-word"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
      }}
      role="tooltip"
      aria-live="polite"
      onClick={(e) => e.stopPropagation()}
    >
      {content}
      {/* Arrow pointer */}
      <div
        className={`absolute h-2 w-2 rotate-45 bg-gray-900 ${
          position === "top"
            ? "bottom-[-4px] left-1/2 -translate-x-1/2"
            : position === "bottom"
            ? "top-[-4px] left-1/2 -translate-x-1/2"
            : position === "left"
            ? "right-[-4px] top-1/2 -translate-y-1/2"
            : "left-[-4px] top-1/2 -translate-y-1/2"
        }`}
      />
    </div>
  );
  return (
    <>
      <div
        className="relative inline-flex items-center"
        ref={triggerRef}
        onClick={handleToggle}
      >
        {showInfoIcon ? (
          <button
            type="button"
            className="text-gray-800 hover:text-gray-900 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
            aria-label="Show information"
            aria-expanded={isVisible}
          >
            <Info size={15} />
          </button>
        ) : (
          children
        )}
      </div>
      {isVisible && tooltipContent}
    </>
  );
};
